<template>
    <div>
        <div v-if="isLoading" class="flex h-40 w-full items-center justify-center">
            <span class="spinner"></span>
        </div>

        <div v-else-if="banners.length === 0" class="flex h-40 w-full items-center justify-center">
            <h3 class="text-sm font-medium text-gray-700">
                No banners found for the selected options
            </h3>
        </div>

        <cube-provider v-else>
            <template v-slot="{ cubeApi }">
                <query-renderer :query="metricsQuery" :cubeApi="cubeApi">
                    <template v-slot="{ loading: metricsLoading, resultSet }">
                        <query-renderer :query="sparklineQuery" :cubeApi="cubeApi">
                            <template v-slot="{ loading: sparklineLoading, resultSet: sparklineResultSet }">
                                <div class="overflow-x-auto pb-2">
                                    <div class="flex gap-8 pl-8">
                                        <div
                                            v-for="banner in banners"
                                            :key="banner.id"
                                            @click="editBanner(banner.id)"
                                            class="banner-card flex-none w-56 sm:w-64 bg-white border border-gray-200 rounded-lg shadow-sm transition-shadow duration-200 cursor-pointer overflow-visible"
                                        >
                                            <div class="relative aspect-[16/9] bg-gray-100 banner-container">
                                                <!-- Main banner card -->
                                                <div class="main-banner-card relative w-full h-full bg-gray-100 rounded-lg overflow-hidden">
                                                    <!-- Banner Image -->
                                                    <img
                                                        v-if="banner.mobileImage"
                                                        :src="banner.mobileImage"
                                                        :alt="banner.name"
                                                        class="absolute inset-0 w-full h-full object-cover"
                                                    />
                                                    <div
                                                        v-else
                                                        class="absolute inset-0 flex items-center justify-center text-gray-400"
                                                    >
                                                        <svg class="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                    </div>
                                                    <div class="absolute top-2 right-2 z-10">
                                                        <span
                                                            class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                                                            :class="getStatusClass(banner)"
                                                        >
                                                            {{ getBannerStatus(banner) }}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="p-2.5">
                                            <h3 class="text-sm font-semibold text-gray-900 mb-2 truncate">
                                                {{ banner.name }}
                                            </h3>

                                            <div class="space-y-2">
                                                <div class="flex items-center justify-between text-xs text-gray-600">
                                                    <span class="flex items-center">
                                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        Start Date
                                                    </span>
                                                    <span class="font-medium">{{ formatDate(banner.startDate) }}</span>
                                                </div>

                                                <div class="flex items-center justify-between text-xs text-gray-600">
                                                    <span class="flex items-center">
                                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                        </svg>
                                                        End Date
                                                    </span>
                                                    <span class="font-medium">{{ banner.endDate ? formatDate(banner.endDate) : 'No end date' }}</span>
                                                </div>

                                                <div class="flex items-center justify-between text-xs text-gray-600">
                                                    <span class="flex items-center">
                                                        <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                                                        </svg>
                                                        Views + Clicks
                                                    </span>
                                                    <span class="font-medium">
                                                        <span v-if="metricsLoading" class="inline-block h-3 w-3 spinner-small"></span>
                                                        <span v-else>{{ getMetricValue(banner.id, resultSet) }}</span>
                                                    </span>
                                                </div>

                                                <!-- Sparkline -->
                                                <div class="pt-2">
                                                    <div v-if="sparklineLoading" class="flex items-center justify-center h-8">
                                                        <span class="inline-block h-3 w-3 spinner-small"></span>
                                                    </div>
                                                    <sparkline
                                                        v-else-if="sparklineDataMap(sparklineResultSet)[banner.id] && sparklineDataMap(sparklineResultSet)[banner.id].length"
                                                        :data="sparklineDataMap(sparklineResultSet)[banner.id]"
                                                        :width="220"
                                                        :height="32"
                                                        :stroke-width="1.5"
                                                        class="w-full"
                                                    />
                                                    <div v-else class="flex items-center justify-center h-8 text-xs text-gray-400">
                                                        No data
                                                    </div>
                                                </div>
                                            </div>

                                            <div class="mt-3 pt-2 border-t border-gray-100 text-xs text-gray-500">
                                                Updated {{ formatDateRelative(banner.updated_at) }}
                                            </div>
                                        </div>
                                        </div>
                                    </div>
                                </div>
                            </template>
                        </query-renderer>
                    </template>
                </query-renderer>
            </template>
        </cube-provider>
    </div>
</template>

<script>
import { CubeProvider, QueryRenderer } from '@/components/cubejs';
import Sparkline from '@/components/widgets/Sparkline';

export default {
    name: 'BannerGrid',
    components: {
        CubeProvider,
        QueryRenderer,
        Sparkline
    },
    props: {
        banners: {
            type: Array,
            required: true,
            default: () => []
        },
        isLoading: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            lastSparklineResultSet: null,
            cachedSparklineMap: null
        };
    },
    computed: {
        bannerIds() {
            return this.banners.map(banner => String(banner.id));
        },
        metricsQuery() {
            if (!this.bannerIds.length) {
                return null;
            }
            
            return {
                measures: ['events.count'],
                dimensions: ['events.offer_id'],
                filters: [
                    {
                        member: 'events.offer_id',
                        operator: 'equals',
                        values: this.bannerIds
                    },
                    {
                        member: 'events.event',
                        operator: 'equals',
                        values: ['viewed_offer', 'clicked_offer']
                    }
                ]
            };
        },
        sparklineQuery() {
            if (!this.bannerIds.length) {
                return null;
            }

            const endDate = new Date();
            const startDate = new Date();
            startDate.setDate(startDate.getDate() - 30);

            return {
                measures: ['events.count'],
                timeDimensions: [
                    {
                        dimension: 'events.timestamp',
                        granularity: 'day',
                        dateRange: [startDate.toISOString(), endDate.toISOString()]
                    }
                ],
                dimensions: ['events.offer_id'],
                filters: [
                    {
                        member: 'events.offer_id',
                        operator: 'equals',
                        values: this.bannerIds
                    },
                    {
                        member: 'events.event',
                        operator: 'equals',
                        values: ['viewed_offer', 'clicked_offer']
                    }
                ]
            };
        }
    },
    methods: {
        getMetricValue(bannerId, resultSet) {
            if (!resultSet) {
                return 0;
            }
            
            const bannerIdStr = String(bannerId);
            
            const data = resultSet.rawData();
            const row = data.find(r => 
                String(r['events.offer_id']) === bannerIdStr
            );
            
            if (!row) {
                return 0;
            }
            
            const count = row['events.count'];
            return typeof count === 'number' ? count : parseInt(count, 10) || 0;
        },
        sparklineDataMap(resultSet) {
            // Cache the sparkline data map by resultSet reference to avoid recomputation
            if (this.lastSparklineResultSet === resultSet && this.cachedSparklineMap) {
                return this.cachedSparklineMap;
            }

            const map = Object.create(null);

            if (!resultSet) {
                this.lastSparklineResultSet = resultSet;
                this.cachedSparklineMap = map;
                return map;
            }

            const data = resultSet.rawData();

            // Group data by banner ID
            data.forEach(row => {
                const bannerId = String(row['events.offer_id']);
                if (!map[bannerId]) {
                    map[bannerId] = [];
                }
                map[bannerId].push(row);
            });

            // Sort and extract values for each banner
            Object.keys(map).forEach(bannerId => {
                const bannerData = map[bannerId]
                    .sort((a, b) => {
                        const dateA = new Date(a['events.timestamp.day']);
                        const dateB = new Date(b['events.timestamp.day']);
                        return dateA - dateB;
                    })
                    .map(row => {
                        const count = row['events.count'];
                        return typeof count === 'number' ? count : parseInt(count, 10) || 0;
                    });
                map[bannerId] = bannerData;
            });

            this.lastSparklineResultSet = resultSet;
            this.cachedSparklineMap = map;
            return map;
        },
        getSparklineData(bannerId, resultSet) {
            // Deprecated: Use sparklineDataMap instead for better performance
            if (!resultSet) {
                return [];
            }

            const bannerIdStr = String(bannerId);
            const data = resultSet.rawData();
            
            // Filter data for this specific banner and sort by date
            const bannerData = data
                .filter(r => String(r['events.offer_id']) === bannerIdStr)
                .sort((a, b) => {
                    const dateA = new Date(a['events.timestamp.day']);
                    const dateB = new Date(b['events.timestamp.day']);
                    return dateA - dateB;
                });

            // Extract values for the sparkline
            return bannerData.map(row => {
                const count = row['events.count'];
                return typeof count === 'number' ? count : parseInt(count, 10) || 0;
            });
        },
        editBanner(bannerId) {
            this.$router.push({ name: 'editOffer', params: { id: bannerId } });
        },
        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return this.$moment(dateString).format('MMM Do YYYY');
        },
        formatDateRelative(dateString) {
            if (!dateString) return 'N/A';
            return this.$moment(dateString).fromNow();
        },
        getBannerStatus(banner) {
            if (banner.archived) {
                return 'Archived';
            }
            const date = new Date();
            if (new Date(banner.startDate) > date) {
                return 'Scheduled';
            } else if (banner.endDate && new Date(banner.endDate) < date) {
                return 'Expired';
            } else {
                return 'Active';
            }
        },
        getStatusClass(banner) {
            const status = this.getBannerStatus(banner);
            switch (status) {
                case 'Active':
                    return 'bg-green-100 text-green-800';
                case 'Scheduled':
                    return 'bg-blue-100 text-blue-800';
                case 'Expired':
                    return 'bg-red-100 text-red-800';
                case 'Archived':
                    return 'bg-gray-100 text-gray-800';
                default:
                    return 'bg-gray-100 text-gray-800';
            }
        }
    }
};
</script>

<style scoped>
.spinner {
    border: 2px solid #f3f3f3;
    border-top: 2px solid #3498db;
    border-radius: 50%;
    width: 24px;
    height: 24px;
    animation: spin 1s linear infinite;
}

.spinner-small {
    border: 1px solid #f3f3f3;
    border-top: 1px solid #3498db;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
}

.banner-card {
    overflow: visible !important;
}

.banner-container {
    position: relative;
}

.main-banner-card {
    position: relative;
    z-index: 10;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    transition: all 0.3s ease-out;
}

.banner-card:hover .main-banner-card {
    box-shadow: 0 8px 16px rgba(0, 0, 0, 0.15);
}
</style>
